// Procedural textures and the shared material library. Everything is generated on canvases at load time (VIS-267:
// or comes baked in the package, painted.js), so the map ships no texture files of its own.
// WS-75: node materials (WebGPURenderer), the looks in TSL: the snow tint, the
// sparkle and the specular cap are lighting hooks (gpu/lighting.js, where lazy/lightcast.js adds the cast light too);
// the interior-mapped window, the uplight mask and the lamp tint are emissive nodes.
// WP5.1a: the lit library is ONE program family (gpu/surface.js): its optional parts (maps, glow mask, room, snow tint,
// sparkle) are values of the shared graph, never a program of their own.
import * as THREE from 'three/webgpu';
import {
  uniform, attribute, vec3, dot, floor, fract, sin, step, smoothstep, min, length, normalize, fwidth, mix, clamp,
  positionWorld, positionView, materialColor, materialEmissive,
} from 'three/tsl';
import { addLightHook, specCap } from './gpu/lighting.js';
import { roomRadiance, SNOW_TINT, SURF_SNOW, SURF_SPARKLE, FAMILY, familyTerms, surfaceOf } from './gpu/surface.js';
import { createPainter } from './painted.js';

// ---- painted pictures (begin): the code that paints the material maps. A change here asks for a new bake
// (node tools/bake_material_maps.mjs <id>; tests/unit/runtime/painted.test.mjs compares each package's maps.json).
function canvas(w, h, draw) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  draw(c.getContext('2d'), w, h);
  return c;
}

// Deterministic noise so textures look the same on every load.
let seed = 1234;
const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);

/** Height canvas -> tangent-space normal map. */
function normalFromHeight(src, strength = 2) {
  const w = src.width, h = src.height;
  const s = src.getContext('2d').getImageData(0, 0, w, h).data;
  const out = new ImageData(w, h);
  const H = (x, y) => s[(((y + h) % h) * w + ((x + w) % w)) * 4] / 255;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (H(x + 1, y) - H(x - 1, y)) * strength, dy = (H(x, y + 1) - H(x, y - 1)) * strength;
      const n = new THREE.Vector3(-dx, -dy, 1).normalize();
      const i = (y * w + x) * 4;
      out.data[i] = (n.x * 0.5 + 0.5) * 255; out.data[i + 1] = (n.y * 0.5 + 0.5) * 255; out.data[i + 2] = (n.z * 0.5 + 0.5) * 255; out.data[i + 3] = 255;
    }
  }
  return canvas(w, h, (g) => g.putImageData(out, 0, 0));
}

function tex(c, { repeat = [1, 1], srgb = true } = {}) {
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(...repeat);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** Horizontal board cladding: one texture tile = 1 m x 1 m, boards 14 cm high. */
function paintBoards(base, { boardH = 18, grain = 0.18, gap = 'rgba(0,0,0,0.55)' } = {}) {
  const color = canvas(256, 256, (g, w, h) => {
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    for (let y = 0; y < h; y += boardH) {
      const shade = (rnd() - 0.5) * 0.18;
      g.fillStyle = shade > 0 ? `rgba(255,255,255,${shade})` : `rgba(0,0,0,${-shade})`;
      g.fillRect(0, y, w, boardH);
      for (let k = 0; k < 14; k++) {
        g.strokeStyle = `rgba(0,0,0,${grain * rnd()})`;
        g.lineWidth = 0.6 + rnd();
        g.beginPath();
        const yy = y + 2 + rnd() * (boardH - 4);
        g.moveTo(0, yy);
        for (let x = 0; x <= w; x += 32) g.lineTo(x, yy + (rnd() - 0.5) * 2.2);
        g.stroke();
      }
      g.fillStyle = gap; g.fillRect(0, y + boardH - 2, w, 2);
      g.fillStyle = 'rgba(255,255,255,0.07)'; g.fillRect(0, y, w, 1);
    }
  });
  const height = canvas(256, 256, (g, w, h) => {
    g.fillStyle = '#fff'; g.fillRect(0, 0, w, h);
    for (let y = 0; y < h; y += boardH) {
      const grd = g.createLinearGradient(0, y, 0, y + boardH);
      grd.addColorStop(0, '#fff'); grd.addColorStop(0.85, '#b8b8b8'); grd.addColorStop(1, '#000');
      g.fillStyle = grd; g.fillRect(0, y, w, boardH);
    }
  });
  return { map: tex(color), normalMap: tex(normalFromHeight(height, 3), { srgb: false }) };
}

/** Diagonal shingle pattern used on the tower entrance gables. */
function paintShingles(base) {
  const c = canvas(256, 256, (g, w, h) => {
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    g.strokeStyle = 'rgba(40,20,5,0.55)'; g.lineWidth = 3;
    for (let k = -h; k < w + h; k += 28) {
      g.beginPath(); g.moveTo(k, 0); g.lineTo(k + h, h); g.stroke();
      g.beginPath(); g.moveTo(k, h); g.lineTo(k + h, 0); g.stroke();
    }
  });
  return tex(c);
}

/** Wind-packed snow: soft large-scale undulation with fine grain. */
function paintSnowNormal() {
  const height = canvas(512, 512, (g, w, h) => {
    g.fillStyle = '#808080'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 900; i++) {
      const x = rnd() * w, y = rnd() * h, r = 6 + rnd() * 40;
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      const v = rnd() > 0.5 ? 255 : 0;
      grd.addColorStop(0, `rgba(${v},${v},${v},0.10)`); grd.addColorStop(1, `rgba(${v},${v},${v},0)`);
      g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    }
    const img = g.getImageData(0, 0, w, h);
    for (let i = 0; i < img.data.length; i += 4) { const n = (rnd() - 0.5) * 26; img.data[i] += n; img.data[i + 1] += n; img.data[i + 2] += n; }
    g.putImageData(img, 0, 0);
  });
  return tex(normalFromHeight(height, 1.6), { srgb: false });
}
// ---- painted pictures (end)

export const textures = {};

/**
 * Snow shading shared by the terrain and snowy roofs (WS-4): shaded snow picks up a little of the sky
 * colour, like blue-hour light scattering in snow. (Sun glints were tried and dropped: sunlit snow already
 * sits at the top of the ACES curve, so they never showed; see JOURNAL 2026-10-11.)
 * The tint uniform is updated from the time of day in main.js applyTime().
 */
export const snowFx = { tint: SNOW_TINT }; // WS-75: a TSL uniform (main.js sets tint.value); VIS-267: shared, uploaded once a render on every set (checked by the live-uniforms unit test)
/** A node material for a classic one (MeshStandardMaterial -> MeshStandardNodeMaterial, ...); node materials pass. */
export function asNodeMaterial(m) {
  if (!m || m.isNodeMaterial) return m;
  const C = m.isMeshPhysicalMaterial ? THREE.MeshPhysicalNodeMaterial : m.isMeshStandardMaterial ? THREE.MeshStandardNodeMaterial
    : m.isMeshBasicMaterial ? THREE.MeshBasicNodeMaterial : m.isMeshLambertMaterial ? THREE.MeshLambertNodeMaterial : null;
  return C ? new C().copy(m) : m;
}
export function addSnowShading(material) {
  // VIS-165 deeper winter shade (resort.json render.daylight): the shade tint is a share of the snow's fill in the sky
  // light there (daylight.js snowFill), so the snow is only marked and gets no tint hook
  if (snowFx.daylight) { const m = asNodeMaterial(material); m.userData.dlSnow = true; return m; }
  // WP5.1a: the hook as before, and the mark (SURF_SNOW) the shared graph reads: a material of the surface family takes the
  // tint there (gpu/surface.js snowTerm, in a uniform If) and this hook writes nothing; any other keeps exactly its old
  // text. A clone has neither, as a clone never had the hook.
  const m = asNodeMaterial(material);
  m[SURF_SNOW] = 1;
  return addLightHook(m, {
    key: 'snow-v2',
    finish: ({ material: me, reflectedLight, outgoingLight, diffuseColor }) => {
      if (surfaceOf(me)?.family) return;
      const lit = dot(reflectedLight.directDiffuse, vec3(0.3333));
      outgoingLight.addAssign(snowFx.tint.mul(diffuseColor.rgb).mul(smoothstep(0.05, 0.5, lit).oneMinus()));
    },
  });
}

// ---- WS-37 (begin): material look levels (resort.json "materials": { "level": "subtle" | "rich" }) -----------------
// Off by default (live look unchanged). A level swaps the textures of the SAME shared materials (buildings are batched
// per material, so draw calls stay flat), turns windowWarm into an interior-mapped window and adds a soft snow sparkle.
// All textures are tiny canvases (<= 512 px, tileable, mipmapped, max anisotropy); 1 texture tile = 2 m (boxUV).
const LOOKS = {
  // wood = board stain (subtle keeps the live colour; rich is one step lighter so the grain reads at night too)
  subtle: { wood: '#3c2d25', contrast: 0.7, woodN: 0.8, shingleN: 0.7, snowN: 0.45, sparkle: 0.4, env: 1.0, room: 1.0, frame: 0.12 },
  rich: { wood: '#4f3c30', contrast: 1.15, woodN: 1.5, shingleN: 1.2, snowN: 0.8, sparkle: 1.0, env: 1.8, room: 1.2, frame: 0.2 },
};

// ---- painted pictures (begin)
const S = 512; // texture size (px) = 2 m
const shade = (g, x, y, w, h, v) => { g.fillStyle = v > 0 ? `rgba(255,236,214,${v})` : `rgba(0,0,0,${-v})`; g.fillRect(x, y, w, h); };
/** A wavy line that tiles horizontally (period S). */
function waveLine(g, y, amp, k, ph) {
  g.beginPath();
  for (let x = 0; x <= S; x += 8) { const yy = y + amp * Math.sin((2 * Math.PI * k * x) / S + ph); if (x) g.lineTo(x, yy); else g.moveTo(x, yy); }
  g.stroke();
}

/** Horizontal board cladding: per-board tone, grain, knots, staggered butt joints, shadow line under each board. */
function paintBoardsTex(base, { rows = 13, contrast = 1, gap = 0.6 } = {}) {
  const bh = S / rows, joints = [];
  const color = canvas(S, S, (g) => {
    g.fillStyle = base; g.fillRect(0, 0, S, S);
    for (let r = 0; r < rows; r++) {
      const y = r * bh;
      shade(g, 0, y, S, bh, (rnd() - 0.5) * 0.26 * contrast);
      for (let k = 0; k < 26; k++) { // grain: light and dark streaks along the board
        const light = rnd() > 0.55;
        g.strokeStyle = light ? `rgba(255,228,196,${(0.03 + rnd() * 0.07) * contrast})` : `rgba(0,0,0,${(0.05 + rnd() * 0.12) * contrast})`;
        g.lineWidth = 0.6 + rnd() * 1.6;
        waveLine(g, y + 2 + rnd() * (bh - 5), 0.6 + rnd() * 1.6, 1 + Math.floor(rnd() * 3), rnd() * 6.3);
      }
      if (rnd() < 0.35) { // a knot
        const kx = rnd() * S, ky = y + bh * (0.3 + rnd() * 0.4), kr = 3 + rnd() * 4;
        const grd = g.createRadialGradient(kx, ky, 0, kx, ky, kr * 2.2);
        grd.addColorStop(0, `rgba(20,10,4,${0.55 * contrast})`); grd.addColorStop(0.45, `rgba(20,10,4,${0.25 * contrast})`); grd.addColorStop(1, 'rgba(20,10,4,0)');
        g.fillStyle = grd; g.beginPath(); g.ellipse(kx, ky, kr * 2.2, kr * 1.2, 0, 0, Math.PI * 2); g.fill();
      }
      const jx = ((r * 0.38 + rnd() * 0.2) % 1) * S; // butt joint, staggered row to row
      joints.push([jx, y]);
      g.fillStyle = `rgba(0,0,0,${0.55 * gap})`; g.fillRect(jx, y, 2, bh);
      g.fillStyle = 'rgba(255,236,214,0.08)'; g.fillRect(jx + 2, y, 1, bh);
      g.fillStyle = `rgba(0,0,0,${gap})`; g.fillRect(0, y + bh - 3, S, 3); // shadow line under the board above
      g.fillStyle = `rgba(255,236,214,${0.1 * contrast})`; g.fillRect(0, y, S, 1);
    }
  });
  const height = canvas(S, S, (g) => {
    for (let r = 0; r < rows; r++) {
      const y = r * bh, grd = g.createLinearGradient(0, y, 0, y + bh);
      grd.addColorStop(0, '#ffffff'); grd.addColorStop(0.8, '#c4c4c4'); grd.addColorStop(0.93, '#7a7a7a'); grd.addColorStop(1, '#000000');
      g.fillStyle = grd; g.fillRect(0, y, S, bh);
    }
    g.fillStyle = '#000'; for (const [jx, y] of joints) g.fillRect(jx, y, 2, bh);
  });
  return { map: tex(color), normalMap: tex(normalFromHeight(height, 3), { srgb: false }) };
}

/** Round logs (log huts, kota walls): rounded shading per log, dark chinks, end grain streaks. ~20 cm logs. */
function paintLogsTex(base, { rows = 10, contrast = 1 } = {}) {
  const bh = S / rows;
  const color = canvas(S, S, (g) => {
    g.fillStyle = base; g.fillRect(0, 0, S, S);
    for (let r = 0; r < rows; r++) {
      const y = r * bh, grd = g.createLinearGradient(0, y, 0, y + bh);
      grd.addColorStop(0, `rgba(0,0,0,${0.35 * contrast})`); grd.addColorStop(0.3, `rgba(255,236,214,${0.12 * contrast})`);
      grd.addColorStop(0.55, 'rgba(0,0,0,0)'); grd.addColorStop(1, `rgba(0,0,0,${0.5 * contrast})`);
      g.fillStyle = grd; g.fillRect(0, y, S, bh);
      shade(g, 0, y, S, bh, (rnd() - 0.5) * 0.18 * contrast);
      for (let k = 0; k < 18; k++) {
        g.strokeStyle = `rgba(0,0,0,${(0.04 + rnd() * 0.1) * contrast})`; g.lineWidth = 0.7 + rnd();
        waveLine(g, y + 4 + rnd() * (bh - 8), 0.8 + rnd(), 1 + Math.floor(rnd() * 2), rnd() * 6.3);
      }
    }
  });
  const height = canvas(S, S, (g) => {
    for (let r = 0; r < rows; r++) {
      for (let i = 0; i < bh; i++) { const t = (i + 0.5) / bh * 2 - 1, v = Math.round(255 * Math.sqrt(Math.max(0, 1 - t * t))); g.fillStyle = `rgb(${v},${v},${v})`; g.fillRect(0, r * bh + i, S, 1); }
    }
  });
  return { map: tex(color), normalMap: tex(normalFromHeight(height, 2.5), { srgb: false }) };
}

/** Felt / wood tab shingles: rows of tabs (staggered slots), tab tone variation, granules, shadow under each row.
 *  Canvas top = up-slope (roof UVs put v up the slope). */
function paintShingleTex(base, { rows = 16, tabs = 6, contrast = 1 } = {}) {
  const bh = S / rows, tw = S / tabs;
  const color = canvas(S, S, (g) => {
    g.fillStyle = base; g.fillRect(0, 0, S, S);
    for (let r = 0; r < rows; r++) {
      const y = r * bh, off = (r % 2) * tw / 2;
      for (let k = -1; k < tabs; k++) {
        const x = k * tw + off;
        shade(g, x, y, tw, bh, (rnd() - 0.5) * 0.2 * contrast);
        g.fillStyle = `rgba(0,0,0,${0.5 * contrast})`; g.fillRect(x, y, 2, bh); // slot between tabs
      }
      const grd = g.createLinearGradient(0, y + bh - 6, 0, y + bh);
      grd.addColorStop(0, 'rgba(0,0,0,0)'); grd.addColorStop(1, `rgba(0,0,0,${0.55 * contrast})`);
      g.fillStyle = grd; g.fillRect(0, y + bh - 6, S, 6);
    }
    const img = g.getImageData(0, 0, S, S); // granules
    for (let i = 0; i < img.data.length; i += 4) { const n = (rnd() - 0.5) * 18 * contrast; img.data[i] += n; img.data[i + 1] += n; img.data[i + 2] += n; }
    g.putImageData(img, 0, 0);
  });
  const height = canvas(S, S, (g) => {
    for (let r = 0; r < rows; r++) {
      const y = r * bh, grd = g.createLinearGradient(0, y, 0, y + bh);
      grd.addColorStop(0, '#202020'); grd.addColorStop(0.92, '#ffffff'); grd.addColorStop(1, '#101010');
      g.fillStyle = grd; g.fillRect(0, y, S, bh);
      g.fillStyle = '#303030';
      for (let k = -1; k < tabs; k++) g.fillRect(k * tw + (r % 2) * tw / 2, y, 2, bh);
    }
  });
  return { map: tex(color), normalMap: tex(normalFromHeight(height, 1.6), { srgb: false }) };
}

/** Standing-seam metal: a low rib every 0.5 m (u = along the eave), faint vertical streaks. */
function paintMetalTex(base) {
  const color = canvas(S, S, (g) => {
    g.fillStyle = base; g.fillRect(0, 0, S, S);
    for (let x = 0; x < S; x += 2) { g.fillStyle = `rgba(255,255,255,${rnd() * 0.035})`; g.fillRect(x, 0, 2, S); }
    for (let x = 0; x < S; x += S / 4) { g.fillStyle = 'rgba(255,255,255,0.14)'; g.fillRect(x, 0, 2, S); g.fillStyle = 'rgba(0,0,0,0.35)'; g.fillRect(x + 2, 0, 3, S); }
  });
  const height = canvas(S, S, (g) => {
    g.fillStyle = '#000'; g.fillRect(0, 0, S, S);
    for (let x = 0; x < S; x += S / 4) { const grd = g.createLinearGradient(x - 5, 0, x + 5, 0); grd.addColorStop(0, '#000'); grd.addColorStop(0.5, '#fff'); grd.addColorStop(1, '#000'); g.fillStyle = grd; g.fillRect(x - 5, 0, 10, S); }
  });
  return { map: tex(color), normalMap: tex(normalFromHeight(height, 2), { srgb: false }) };
}

/** Wind-packed snow v2: soft drifts + elongated wind ripples (sastrugi) + fine grain. */
function paintSnowNormal2() {
  const height = canvas(S, S, (g, w, h) => {
    g.fillStyle = '#808080'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 700; i++) {
      const x = rnd() * w, y = rnd() * h, r = 8 + rnd() * 46, v = rnd() > 0.5 ? 255 : 0;
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, `rgba(${v},${v},${v},0.10)`); grd.addColorStop(1, `rgba(${v},${v},${v},0)`);
      g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    }
    for (let i = 0; i < 160; i++) { // ripples: long thin ridges along one wind direction
      const x = rnd() * w, y = rnd() * h, l = 20 + rnd() * 60;
      g.strokeStyle = `rgba(255,255,255,${0.05 + rnd() * 0.07})`; g.lineWidth = 1 + rnd() * 2;
      g.beginPath(); g.moveTo(x, y); g.quadraticCurveTo(x + l / 2, y + (rnd() - 0.5) * 6, x + l, y + (rnd() - 0.5) * 4); g.stroke();
    }
    const img = g.getImageData(0, 0, w, h);
    for (let i = 0; i < img.data.length; i += 4) { const n = (rnd() - 0.5) * 34; img.data[i] += n; img.data[i + 1] += n; img.data[i + 2] += n; }
    g.putImageData(img, 0, 0);
  });
  return tex(normalFromHeight(height, 1.8), { srgb: false });
}
// ---- painted pictures (end)

/**
 * Soft snow sparkle: a sparse set of world-space cells (about 7 cm) that glint as the view moves. It scales with the
 * light that reaches the snow (so it is faint at night), fades out where a cell gets smaller than a pixel (no
 * shimmering far away) and is capped well below the lamp level (no bloom fireflies).
 */
export function addSparkle(material, strength) { // (exported for the surface-late unit test)
  // WP5.1a: the hook as before, and the strength (SURF_SPARKLE) the shared graph reads: a material of the surface family
  // takes the same cells there (gpu/surface.js sparkleTerm, in a uniform If) and this hook writes nothing. A clone has
  // neither, as a clone never had the hook (glowingSnow's copy of the snow does not sparkle). A sparkle added after the
  // family's first build is no term of its programs (familyTerms refuses it): then the hook draws it, as before.
  if (surfaceOf(material)?.family) familyTerms({ sparkle: true });
  material[SURF_SPARKLE] = strength;
  const k = uniform(strength);
  return addLightHook(material, {
    key: 'sparkle',
    finish: ({ material: me, reflectedLight, outgoingLight }) => {
      if (surfaceOf(me)?.family && FAMILY.sparkle) return;
      const cell = floor(positionWorld.mul(14.0));
      const h = fract(sin(dot(cell, vec3(12.9898, 78.233, 37.719))).mul(43758.5453));
      const vd = floor(normalize(positionView.negate()).mul(7.0)); // the direction to the eye, in 7 steps
      const tw = fract(h.mul(91.7).add(dot(vd, vec3(0.131, 0.217, 0.373))));
      const fade = smoothstep(0.012, 0.05, length(fwidth(positionWorld))).oneMinus();
      const lum = dot(reflectedLight.directDiffuse.add(reflectedLight.indirectDiffuse), vec3(0.3333));
      const s = step(0.992, h).mul(smoothstep(0.75, 1.0, tw)).mul(fade).mul(k);
      outgoingLight.addAssign(vec3(s.mul(min(lum.mul(1.6), 0.9))));
    },
  });
}

/**
 * Interior-mapped window (windowWarm): a room behind the pane, ray-traced per pixel in the facade frame (Kit
 * facadeUV: u = metres along the wall, v = room height 0..3). Cells 4 m wide and 5 m deep (floor, ceiling, back and
 * side walls), a soft night glow (bright ceiling and upper back wall, darker floor and corners, a little furniture
 * shadow), per-room brightness variation. The emissive colour carries the night ramp (setLightLevel); the dark,
 * glossy base gives a Fresnel sky reflection by day. No new geometry, one material for every window.
 * The room is generic, so it only suits small windows. Large glazing is real glass with the building's interior
 * model behind it (bakedInterior, below; compound.js `inside: "open"`) or, with no model, a plain glow
 * (Kit.room(.., plain) lifts v by 100): never an invented room.
 */
// (the room itself: gpu/surface.js roomRadiance, a term of the shared graph)
function roomWindow(material, { gain = 1 } = {}) {
  // the room's radiance x the emissive colour x intensity (the night ramp, setLightLevel) x the look's gain. WP5.1a: the
  // node is tagged (surfRoom), so the shared graph (gpu/surface.js) draws the same room in every surface program, inside
  // a uniform If; a clone (glowScaled) keeps the node and so the room and its gain
  const g = uniform(gain), n = roomRadiance().mul(materialEmissive).mul(g);
  n.surfRoom = g;
  familyTerms({ room: true });
  material.emissiveNode = n;
  material.userData.roomWindow = 'room-window-v2';
  return material;
}

function applyLook(M, level, aniso) {
  const L = LOOKS[level];
  if (!L) return;
  const all = [];
  const wood = boardsTex(L.wood, { contrast: L.contrast });
  const grey = boardsTex('#5d5651', { contrast: L.contrast });
  const pine = boardsTex('#b98a5e', { contrast: L.contrast * 0.8, gap: 0.45 });
  const felt = shingleTex('#3d3b39', { contrast: L.contrast });
  const log = logsTex('#5a4636', { contrast: L.contrast });
  const metal = metalTex('#2b2e33');
  const snowN = snowNormal2();
  all.push(wood.map, wood.normalMap, grey.map, grey.normalMap, pine.map, pine.normalMap, felt.map, felt.normalMap, log.map, log.normalMap, metal.map, metal.normalMap, snowN);
  for (const t of all) t.anisotropy = aniso;
  const nScale = (k) => new THREE.Vector2(k, k);
  Object.assign(M.wood, { map: wood.map, normalMap: wood.normalMap, normalScale: nScale(L.woodN) });
  Object.assign(M.woodGrey, { map: grey.map, normalMap: grey.normalMap, normalScale: nScale(L.woodN) });
  Object.assign(M.pine, { map: pine.map, normalMap: pine.normalMap, normalScale: nScale(L.woodN * 0.8) });
  Object.assign(M.deck, { map: pine.map, normalMap: pine.normalMap, normalScale: nScale(L.woodN * 0.8) });
  Object.assign(M.deckLit, { map: pine.map, emissiveMap: pine.map });
  Object.assign(M.shingleDark, { map: felt.map, normalMap: felt.normalMap, normalScale: nScale(L.shingleN) });
  M.snow.normalMap = snowN; M.snow.normalScale = nScale(L.snowN);
  addSparkle(M.snow, L.sparkle);
  // log walls (models that say "log walls") and the metal band of glass-kota roofs: library materials, one draw call each
  M.log = new THREE.MeshStandardNodeMaterial({ map: log.map, normalMap: log.normalMap, normalScale: nScale(L.woodN), roughness: 0.85 });
  M.metalRoof = new THREE.MeshStandardNodeMaterial({ map: metal.map, normalMap: metal.normalMap, normalScale: nScale(0.6), roughness: 0.45, metalness: 0.55 });
  M.metalRoof.userData.slopeUV = true;
  // windows: interior-mapped room + glossy pane (sky reflection by day); white emissive, the room carries the warmth
  M.windowWarm.color.set(0x0b0d10); M.windowWarm.roughness = 0.06; M.windowWarm.metalness = 0; M.windowWarm.envMapIntensity = L.env;
  M.windowWarm.emissive.set(0xffffff);
  M.windowWarm.userData.facadeUV = true;
  roomWindow(M.windowWarm, { gain: L.room });
  Object.defineProperty(M, 'look', { value: { level, ...L }, enumerable: false }); // read by builders (frame depth)
}
// ---- WS-37 (end) -----------------------------------------------------------------------------------------------
// WS-63 materials: the generators for lazy/matlook.js (resort.materials add / library), run on a private seed so no
// texture made before or after them changes.
// VIS-267: every generator goes through the recipe cache (painted.js): its arguments and the seed it starts from name
// its pictures; a recipe the package has baked is not painted (the seed is left where painting would leave it).
const PAINT = createPainter({ get: () => seed, set: (v) => { seed = v; } });
/** paint(recipe, make): a generator's pictures by recipe (also lazy/matlook.js); useBakedMaps / paintedReady / paintedUnasked: main.js. */
export const painted = PAINT.paint, useBakedMaps = PAINT.use, paintedReady = PAINT.ready, paintedUnasked = PAINT.unasked;
const recipe = (name, f) => (...a) => PAINT.paint([name, a, seed], () => f(...a));
const boards = recipe('boards', paintBoards), shingles = recipe('shingles', paintShingles), snowNormal = recipe('snowNormal', paintSnowNormal);
const boardsTex = recipe('boardsTex', paintBoardsTex), logsTex = recipe('logsTex', paintLogsTex), shingleTex = recipe('shingleTex', paintShingleTex);
const metalTex = recipe('metalTex', paintMetalTex), snowNormal2 = recipe('snowNormal2', paintSnowNormal2);
export const GEN = { boards: boardsTex, logs: logsTex, shingle: shingleTex, metal: metalTex };
export function seeded(s, f) { const k = seed; seed = s; try { return f(); } finally { seed = k; } }
/** WS-75: WebGPURenderer.getMaxAnisotropy() (its WebGL2 backend knows it only after renderer.init(): 8 until then). */
export function maxAnisotropy(renderer) { try { return renderer?.getMaxAnisotropy?.() || 8; } catch { return 8; } }

export function makeMaterials(renderer, look = null) {
  const aniso = maxAnisotropy(renderer);
  const darkWood = boards('#3a2c25');
  const pine = boards('#b9875a', { grain: 0.25, gap: 'rgba(60,30,10,0.45)' });
  const greyWood = boards('#5b544f');
  textures.snowNormal = snowNormal();
  for (const t of [darkWood.map, darkWood.normalMap, pine.map, pine.normalMap, greyWood.map, textures.snowNormal]) t.anisotropy = aniso;

  const M = {
    wood: new THREE.MeshStandardNodeMaterial({ map: darkWood.map, normalMap: darkWood.normalMap, normalScale: new THREE.Vector2(0.7, 0.7), roughness: 0.82 }),
    woodGrey: new THREE.MeshStandardNodeMaterial({ map: greyWood.map, normalMap: darkWood.normalMap, roughness: 0.85 }),
    pine: new THREE.MeshStandardNodeMaterial({ map: pine.map, normalMap: pine.normalMap, roughness: 0.7 }),
    shingle: new THREE.MeshStandardNodeMaterial({ map: shingles('#a7744a'), roughness: 0.75 }),
    shingleDark: new THREE.MeshStandardNodeMaterial({ map: shingles('#3d3b39'), roughness: 0.85 }), // WS-28: near-black felt shingles (kota.js roofs)
    post: new THREE.MeshStandardNodeMaterial({ color: 0x1e1814, roughness: 0.8 }),
    trim: new THREE.MeshStandardNodeMaterial({ color: 0xe9e6df, roughness: 0.6 }),
    plinth: new THREE.MeshStandardNodeMaterial({ color: 0xc9cbcc, roughness: 0.9 }),
    snow: addSnowShading(new THREE.MeshStandardNodeMaterial({ color: 0xf4f7fb, roughness: 0.92, normalMap: textures.snowNormal, normalScale: new THREE.Vector2(0.35, 0.35) })),
    roofEdge: new THREE.MeshStandardNodeMaterial({ color: 0x22252a, roughness: 0.7 }),
    metal: new THREE.MeshStandardNodeMaterial({ color: 0x15171a, roughness: 0.4, metalness: 0.6 }),
    // Glass: reflective, partly see-through to the lit interior behind it.
    glass: new THREE.MeshPhysicalNodeMaterial({
      color: 0x9fb7c9, metalness: 0, roughness: 0.03, transparent: true, opacity: 0.32,
      envMapIntensity: 1.6, clearcoat: 1, clearcoatRoughness: 0.02, side: THREE.DoubleSide, depthWrite: false,
    }),
    // Interior surfaces: warm light that switches on as daylight fades.
    interior: new THREE.MeshStandardNodeMaterial({ color: 0x8a5a35, emissive: 0xffa04a, emissiveIntensity: 0.6, roughness: 0.8 }),
    linen: new THREE.MeshStandardNodeMaterial({ color: 0xf2ece2, emissive: 0xffc58a, emissiveIntensity: 0.5, roughness: 0.9 }),
    lamp: new THREE.MeshStandardNodeMaterial({ color: 0xffe3b0, emissive: 0xffb860, emissiveIntensity: 2.5 }),
    led: new THREE.MeshStandardNodeMaterial({ color: 0xffd9a0, emissive: 0xffae55, emissiveIntensity: 3 }),
    windowWarm: new THREE.MeshStandardNodeMaterial({ color: 0x2a2017, emissive: 0xffb15e, emissiveIntensity: 1.2, roughness: 0.3 }),
    canvas: new THREE.MeshStandardNodeMaterial({ color: 0xe6e1d6, roughness: 0.95 }),
    water: new THREE.MeshStandardNodeMaterial({ color: 0x0b1a24, roughness: 0.05, metalness: 0.2 }),
    deck: new THREE.MeshStandardNodeMaterial({ map: pine.map, color: 0x8a6e58, roughness: 0.8 }),
    // Kelo: debarked, weathered silver deadwood (Lapland gates, swings, sign frames).
    kelo: new THREE.MeshStandardNodeMaterial({ color: 0x8e867a, roughness: 0.9 }),
    // Uplit variants: floodlit from the ground after sunset (sign walls, gate and swing posts with photo-confirmed uplights).
    keloLit: new THREE.MeshStandardNodeMaterial({ color: 0x8e867a, emissive: 0xff8a40, emissiveIntensity: 0, roughness: 0.9 }),
    deckLit: new THREE.MeshStandardNodeMaterial({ map: pine.map, color: 0x8a6e58, emissive: 0xff7a2a, emissiveMap: pine.map, emissiveIntensity: 0, roughness: 0.8 }),
    // Red neon / LED sign letters: painted red by day, glowing after sunset.
    neon: new THREE.MeshStandardNodeMaterial({ color: 0xc8261a, emissive: 0xff1608, emissiveIntensity: 0.5, roughness: 0.5 }),
    // Backlit / LED sign lettering: legible warm orange (the brighter 'led' is for light strips).
    signLed: new THREE.MeshStandardNodeMaterial({ color: 0xe0782c, emissive: 0xff6a1a, emissiveIntensity: 0.3, roughness: 0.5 }),
  };
  M.skylight = new THREE.MeshPhysicalNodeMaterial({ color: 0x16202b, metalness: 0.2, roughness: 0.04, clearcoat: 1, envMapIntensity: 1.8, emissive: 0xffa04a, emissiveIntensity: 0.05 });
  if (look?.level) applyLook(M, look.level, aniso); // WS-37: preview-gated material look
  // Custom geometry is built without guaranteed winding; render both sides (three flips normals per fragment).
  for (const m of Object.values(M)) m.side = THREE.DoubleSide;
  for (const m of Object.values(M)) capSpecular(m); // WS-15: no punctual-light fireflies on glossy surfaces
  return M;
}

/**
 * WS-15 (round 4e): cap the specular of punctual lights (sun, moon, lamps) on glossy materials.
 * With roughness near 0 the GGX highlight of a directional light is a near-delta: at the mirror angle the moon on
 * cabin glass (roughness 0.03 + clearcoat 0.02) reached luminance ~125 in the HDR buffer (lamps are ~3), and
 * bloom turned that handful of pixels into a big white ball that popped in and out while orbiting.
 * Only the *direct* specular is clamped (environment reflections, diffuse and emissive are untouched), so the
 * glint stays visible as a small highlight. Materials rougher than `maxRoughness` never produce such peaks.
 */
export const SPECULAR_MAX = 2.0;
export function capSpecular(material, { max = SPECULAR_MAX, maxRoughness = 0.35 } = {}) {
  if (!material?.isMeshStandardMaterial || !material.isNodeMaterial || material.userData.specCap) return material;
  const glossy = material.roughness <= maxRoughness || (material.isMeshPhysicalMaterial && material.clearcoat > 0);
  if (!glossy) return material;
  material.userData.specCap = max;
  return specCap(material, max);
}

/** WS-44 (user, 2026-10-12: at night the resort's own lights should be the focal point): the interior window glow
 *  ramps up gently with darkness. resort.json -> lighting = { interiorNightBoost, boostFrom, boostTo }: the glow is
 *  multiplied by 1 + interiorNightBoost * smoothstep(sun elevation from boostFrom down to boostTo, deg). Only the
 *  interiors (windows, rooms, beds seen through glass, skylight glow, and the GLB/tent materials derived from them);
 *  path lamps, signs and the distant landscape lights (WS-32) are untouched. main.js keeps the bloom from growing. */
export const INTERIOR_NIGHT_DEFAULTS = { interiorNightBoost: 0.35, boostFrom: -6, boostTo: -18, bloomHold: 1 };
export function interiorNightGlow(alt, cfg = {}) {
  const c = { ...INTERIOR_NIGHT_DEFAULTS, ...cfg };
  const span = c.boostFrom - c.boostTo;
  const t = span > 0 ? Math.min(1, Math.max(0, (c.boostFrom - alt) / span)) : (alt <= c.boostTo ? 1 : 0);
  return 1 + c.interiorNightBoost * t * t * (3 - 2 * t);
}

/** Room lights (Fine-tune Lights "Room lights", touches `lights.rooms`): how bright the rooms behind the windows glow,
 *  x 0..2 (1 = as built). With the cabins' light level `k`, read by bakedInterior (its daylight floor dims at night). */
export const ROOM_LIGHT = { k: 0, rooms: 1 };

/** Lights inside buildings and along paths brighten as the sun sets. glow = interiorNightGlow() (WS-44).
 *  lampHold (WS-44) = the factor main.js divided the bloom strength by: the lamps are scaled by it so their halo
 *  (bloom = strength x pixel) stays exactly as before while the windows' halo does not grow.
 *  rooms = Room lights: x the inside glow only (rooms, beds, windows, skylights and every material derived from them);
 *  lamps, LEDs, signs, hot tubs and fence lights are outdoor lights (Fine-tune's light items) and stay. */
export function setLightLevel(M, k, glow = 1, lampHold = 1, rooms = 1) {
  ROOM_LIGHT.k = k; ROOM_LIGHT.rooms = rooms;
  glow *= rooms;
  M.interior.emissiveIntensity = (0.15 + 1.5 * k) * glow;
  M.linen.emissiveIntensity = (0.1 + 1.3 * k) * glow;
  M.lamp.emissiveIntensity = (0.3 + 4.5 * k) * lampHold;
  M.led.emissiveIntensity = (0.4 + 4.0 * k) * lampHold;
  M.neon.emissiveIntensity = 0.3 + 1.5 * k; // kept low: ACES + bloom push a hot red to orange
  M.signLed.emissiveIntensity = 0.3 + 1.1 * k;
  M.keloLit.emissiveIntensity = 0.55 * k; // landmark parts mask this per vertex (glow), so the peak sits at the lamps
  M.deckLit.emissiveIntensity = 0.9 * k;
  M.windowWarm.emissiveIntensity = (0.2 + 2.2 * k) * glow;
  // VIS-149: glowing water (a GLB's glow-masked `water` parts with a glow colour: hot tubs) follows the windows' night
  // ramp, faint by day and full after dark (was one fixed strength); the library water's own emissive is black
  M.water.emissiveIntensity = 0.08 + 0.92 * k;
  if (M.skylight) M.skylight.emissiveIntensity = (0.02 + 0.5 * k) * glow;
  for (const d of derivedMaterials) d.emissiveIntensity = M[d.userData.baseKey].emissiveIntensity * (d.userData.gain ?? 1);
}

/**
 * Textured variants of library materials (landmark GLBs that ship their own baked maps, web/js/gltfUnits.js).
 * They keep the base material's look and night ramp (emissive), with the GLB's albedo/normal maps on top.
 */
export const derivedMaterials = [];
// WS-75 perf: ONE node for every glow-masked variant (materialEmissive reads each material's own emissive): a fresh node
// per material gave each its own program cache key, so 4 identical lit programs were built at the start
let glowEmissive = null;
export function deriveTextured(M, key, { map, normalMap, normalScale, glow, gain = 1, color = null } = {}) {
  const base = M[key] || M.wood;
  const m = base.clone();
  m.userData.baseKey = M[key] ? key : 'wood';
  m.userData.gain = gain; // per-landmark uplight strength and colour (resort.json props[].params.glow)
  if (color && m.emissive) m.emissive.set(color);
  if (map) { m.map = map; m.emissiveMap = map; m.color.set(key.startsWith('kelo') ? 0xa8a296 : 0xc4bcb0); } // albedos are bright: kelo reads silver-grey; the night glow keeps the grain
  if (normalMap) { m.normalMap = normalMap; if (normalScale) m.normalScale.copy(normalScale).multiplyScalar(0.6); }
  // per-vertex uplight mask (Blender landmark_kit.glow_mask): the night emission fades up from the real lamps.
  // (A clone shares the base's node slots, so the emissive node is always set here: a base's room window does not carry over.)
  m.emissiveNode = glow ? glowMaskNode() : null;
  derivedMaterials.push(m);
  return m;
}
/** The night emission scaled per vertex by the geometry's 'glow' attribute (0..1). WS-75 perf: one shared node. WP5.1a:
 *  tagged (surfGlow): on a standard material the shared graph (gpu/surface.js) reads the mask, so it is no program of its own. */
export const glowMaskNode = () => (glowEmissive ||= Object.assign(materialEmissive.mul(attribute('glow', 'float')), { surfGlow: true }));
/** Firelight after passing cloth of this colour: the warm fire colour tinted by the cloth's hue (its brightness is the gain's). */
export const fireThrough = (c) => new THREE.Color(0xffa860).multiply(c.clone().multiplyScalar(1 / Math.max(...c.toArray())));
/** Snow plastered on a glowing tent's canvas (`cloth`, a lit tent material): where it is thin (the per-vertex 'glow',
 *  1 at its top edge) the canvas shows through it, in the canvas's colour and glow, so the edge has no line; 0 = plain
 *  snow. userData.snow: winter-only like M.snow (main.js isSnow). One per cloth. */
export function glowingSnow(M, cloth) {
  const id = cloth.uuid;
  M._glowSnow ||= new Map();
  if (!M._glowSnow.has(id)) {
    const m = addSnowShading(M.snow.clone()); // a clone drops the snow light hook: add it again
    m.colorNode = mix(materialColor, uniform(cloth.color), clamp(attribute('glow', 'float').mul(2), 0, 1)); // all canvas at the edge, plain snow lower down
    m.emissive = cloth.emissive.clone(); m.emissiveNode = glowMaskNode(); m.emissiveIntensity = 0;
    Object.assign(m.userData, { snow: true, baseKey: 'interior', gain: cloth.userData.gain });
    derivedMaterials.push(m);
    M._glowSnow.set(id, m);
  }
  return M._glowSnow.get(id);
}

/**
 * A glowing library material at another strength (fairy lights, a building's windows): `gain` scales its emission and
 * it keeps the base's look and night ramp (setLightLevel drives derived materials). One copy per material and gain, so
 * every prop with the same dial shares one draw. gain 1 = the library material itself. Props expose it as `glow`.
 */
const glowCopies = new WeakMap();
export function glowScaled(M, key, gain = 1) {
  const base = M[key];
  if (!base || !(gain >= 0) || gain === 1) return base;
  const per = glowCopies.get(base) || glowCopies.set(base, new Map()).get(base), id = Math.round(gain * 100) / 100;
  if (per.has(id)) return per.get(id);
  const m = base.clone(); // a clone keeps the base's nodes (windowWarm's room window reads materialEmissive, so it dims too)
  m.userData.baseKey = key; m.userData.gain = id;
  m.emissiveIntensity = base.emissiveIntensity * id;
  derivedMaterials.push(m);
  per.set(id, m);
  return m;
}

/**
 * Baked interior (WS-37 r3): the unlit material of a building's interior model (tools/blender/interior_kit.py). The
 * lighting is in the mesh's vertex colours, so the viewer only sets how bright the room is: `day` by daylight, rising
 * with the same night ramp as the windows (`gain` x windowWarm's level). No light, no texture, one draw call.
 */
export function bakedInterior(M, { gain = 0.2, day = 0.25 } = {}) {
  const m = new THREE.MeshBasicNodeMaterial({ vertexColors: true }); // one-sided: the model only has faces that can be seen
  m.userData = { baseKey: 'windowWarm', gain: 1 };
  // driven by setLightLevel (v = windowWarm's level, Room lights included); Room lights also take the daylight floor
  // down as the cabins' lights come on (k), so 0 is a dark room at night and the day look never changes (1 = as before)
  Object.defineProperty(m, 'emissiveIntensity', { get: () => 0, set(v) { m.color.setScalar(day * (1 - ROOM_LIGHT.k * (1 - ROOM_LIGHT.rooms)) + gain * v); } });
  m.emissiveIntensity = M.windowWarm.emissiveIntensity;
  derivedMaterials.push(m);
  return m;
}

/** Radial contact-shadow texture used under buildings and animals. */
export function contactShadowTexture() {
  const c = canvas(128, 128, (g, w) => {
    const grd = g.createRadialGradient(w / 2, w / 2, 0, w / 2, w / 2, w / 2);
    grd.addColorStop(0, 'rgba(20,30,50,0.55)'); grd.addColorStop(0.55, 'rgba(20,30,50,0.25)'); grd.addColorStop(1, 'rgba(20,30,50,0)');
    g.fillStyle = grd; g.fillRect(0, 0, w, w);
  });
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
